import { useState, useEffect } from 'react';
import { settingsAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Eye, EyeOff, Copy, RefreshCw, KeyRound, AlertTriangle, Zap, CheckCircle2, XCircle, Loader2 } from 'lucide-react';

const ENDPOINT_PATH = '/api/integrations/marketing-leads';

export function MarketingIntegrationCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reveal, setReveal] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    try {
      const res = await settingsAPI.getMarketingKey();
      setData(res.data);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load API key settings');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const copyKey = () => {
    if (!data?.key) return;
    navigator.clipboard.writeText(data.key);
    toast.success('API key copied to clipboard');
  };

  const regenerate = async () => {
    setRegenerating(true);
    try {
      const res = await settingsAPI.regenerateMarketingKey();
      setData(res.data);
      setReveal(true);
      setConfirmOpen(false);
      setTestResult(null);
      toast.success('New API key generated. Update your marketing website with this key.');
    } catch (e) {
      console.error(e);
      toast.error('Failed to regenerate key');
    } finally { setRegenerating(false); }
  };

  const runTest = async () => {
    if (!data?.key) return;
    setTesting(true);
    setTestResult(null);
    try {
      const res = await settingsAPI.testMarketingKey(data.key);
      setTestResult({
        ok: true,
        message: res.data?.duplicate
          ? 'Connection successful — endpoint reachable and key accepted.'
          : 'Connection successful! The endpoint is reachable and your key was accepted. Lead intake is ready.',
      });
      settingsAPI.cleanupTestLeads().catch((e) => console.error(e));
    } catch (e) {
      console.error(e);
      const status = e.response?.status;
      const detail = e.response?.data?.detail || e.message;
      setTestResult({
        ok: false,
        message: status === 401
          ? 'Key rejected (401). The endpoint did not accept this key.'
          : `Test failed (${status || 'network error'}): ${detail}`,
      });
    } finally { setTesting(false); }
  };

  const masked = data?.key ? `${data.key.slice(0, 12)}${'•'.repeat(18)}${data.key.slice(-4)}` : '';

  return (
    <Card data-testid="marketing-integration-card">
      <CardHeader>
        <div className="flex items-center gap-2">
          <span className="h-8 w-8 rounded-md bg-primary/10 text-primary flex items-center justify-center">
            <KeyRound size={16} />
          </span>
          <div>
            <CardTitle className="text-lg font-heading">Marketing Lead Intake API</CardTitle>
            <CardDescription>Secret key your marketing website uses to send leads into the CRM</CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-5">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading…</p>
        ) : (
          <>
            <div className="space-y-2">
              <Label>Endpoint (POST)</Label>
              <code className="block text-xs bg-muted rounded-md px-3 py-2 break-all" data-testid="marketing-endpoint">
                https://&lt;your-domain&gt;{ENDPOINT_PATH}
              </code>
              <p className="text-xs text-muted-foreground">Send the key in the <code>X-API-Key</code> header. Use your production domain (e.g. falcon.visaxpress.in).</p>
            </div>

            <div className="space-y-2">
              <Label>API Key</Label>
              <div className="flex gap-2">
                <Input
                  readOnly
                  value={reveal ? (data?.key || '') : masked}
                  className="font-mono text-sm"
                  data-testid="marketing-key-value"
                />
                <Button variant="outline" size="icon" onClick={() => setReveal(r => !r)} data-testid="marketing-key-reveal" title={reveal ? 'Hide' : 'Reveal'}>
                  {reveal ? <EyeOff size={16} /> : <Eye size={16} />}
                </Button>
                <Button variant="outline" size="icon" onClick={copyKey} data-testid="marketing-key-copy" title="Copy">
                  <Copy size={16} />
                </Button>
              </div>
              {data?.updated_at && (
                <p className="text-xs text-muted-foreground" data-testid="marketing-key-meta">
                  Last changed {new Date(data.updated_at).toLocaleString()}{data.updated_by ? ` by ${data.updated_by}` : ''}
                </p>
              )}
              {data?.source === 'env' && (
                <p className="text-xs text-amber-600">Currently using the deployment's environment key. Regenerate to manage it here instead.</p>
              )}
            </div>

            <div className="flex flex-wrap gap-2 pt-2 border-t">
              <Button variant="outline" onClick={runTest} disabled={testing || !data?.key} className="gap-2" data-testid="marketing-key-test">
                {testing ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />}
                {testing ? 'Testing…' : 'Test connection'}
              </Button>
              <Button variant="destructive" onClick={() => setConfirmOpen(true)} className="gap-2" data-testid="marketing-key-regenerate">
                <RefreshCw size={15} /> Regenerate Key
              </Button>
            </div>

            {testResult && (
              <div
                className={`rounded-lg border p-3 flex gap-2 text-sm ${testResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-red-50 border-red-200 text-red-800'}`}
                data-testid="marketing-test-result"
              >
                {testResult.ok ? <CheckCircle2 size={16} className="shrink-0 mt-0.5" /> : <XCircle size={16} className="shrink-0 mt-0.5" />}
                <span>{testResult.message}</span>
              </div>
            )}

            <div className="rounded-lg bg-muted/50 border p-3 flex gap-2">
              <AlertTriangle size={16} className="text-amber-600 shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                Regenerating immediately invalidates the old key. Any marketing website still using the old key will get <strong>401 errors</strong> until you update it with the new key.
              </p>
            </div>
          </>
        )}
      </CardContent>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent data-testid="regenerate-confirm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Regenerate API key?</DialogTitle>
            <DialogDescription>
              This creates a brand-new key and instantly disables the current one. You'll need to give the new key to your marketing website, or lead submissions will fail with 401.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={regenerate} disabled={regenerating} data-testid="regenerate-confirm-button">
              {regenerating ? 'Generating…' : 'Yes, regenerate'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
